import React, { useEffect } from 'react';
import {
  Alert,
  Typography,
  LoadingButton,
  Button,
} from '@one-thd/sui-atomic-components';
import { ItemToReturn } from '@src/models/returns';
import { TagPostPageError } from '@src/utils/analytics-tagging';
import {
  ITEMS_NOT_PROCESSED_MESSAGE,
  ITEMS_NOT_PROCESSED_RETRY_FAILED_ACTION,
  ITEMS_NOT_PROCESSED_RETRY_FAILED_INTRO,
  ITEMS_NOT_PROCESSED_RETRY_FAILED_MESSAGE,
} from '@src/constants/analytics';
import ReturnReviewCard from './summary-cards/ReturnReviewCard';
import ItemImage from './items/ItemImage';
import ItemDescription from './items/ItemDescription';

interface ReturnItemRowProps {
  item: ItemToReturn;
}

export const ReturnItemRow = ({ item }: ReturnItemRowProps) => (
  <div className="sui-flex sui-items-start sui-gap-6">
    <div className="sui-flex sui-w-full">
      <ItemImage
        imageUrl={item.itemImageURL}
        itemDescription={item.itemDescription}
      />
      <div className="sui-flex sui-flex-col">
        <ItemDescription
          brandName={item.brandName}
          itemDescription={item.itemDescription}
        />
      </div>
      <div className="sui-ml-auto sui-text-right">
        <Typography>Qty: {item?.returnQuantity}</Typography>
        <Typography weight="bold">{`$${item.itemCost?.toFixed(2)}`}</Typography>
      </div>
    </div>
  </div>
);

interface ItemsNotProcessedCardProps {
  /** Card heading, e.g. "Items Not Processed" or "UPS Drop-Off Not Processed". */
  header: string;
  /** Items that could not be processed and should be retried. */
  items: ItemToReturn[];
  /** Resubmit handler wired to the "Try Again" button. */
  onRetry: () => void;
  /** When true, the "Try Again" button shows a spinner (retry in progress). */
  retrying?: boolean;
  /**
   * When true, a retry has already failed: show the updated banner copy that
   * points the customer to Order Details instead of the support phone number.
   */
  retryFailed?: boolean;
  /**
   * When provided, render a secondary "Return to Order Details" button below
   * "Try Again" wired to this handler (the post-retry exit path).
   */
  onReturnToOrderDetails?: () => void;
  /**
   * Backend/SSR code for the failed create call, forwarded to the Adobe
   * `post page error` event. Falls back to `n/a` when not available.
   */
  code?: string;
}

/**
 * Shared "not processed" card: a red error Alert, the affected item rows, and a
 * "Try Again" button. Used by the split-return error step and the Keep It timeout
 * screen. After a retry has also failed (`retryFailed` + `onReturnToOrderDetails`),
 * the copy updates and a "Return to Order Details" exit button is shown.
 */
const ItemsNotProcessedCard = ({
  header,
  items,
  onRetry,
  retrying = false,
  retryFailed = false,
  onReturnToOrderDetails,
  code,
}: ItemsNotProcessedCardProps) => {
  const message = retryFailed
    ? ITEMS_NOT_PROCESSED_RETRY_FAILED_MESSAGE
    : ITEMS_NOT_PROCESSED_MESSAGE;

  useEffect(() => {
    TagPostPageError({
      type: 'error',
      pageSection: 'return create partial failure',
      message,
      code,
    });
  }, [message, code]);

  return (
    <ReturnReviewCard header={header}>
      <div className="sui-mb-6">
        <Alert status="error" size="large">
          {retryFailed ? (
            <div className="sui-space-y-4">
              <Typography variant="body-base">
                {ITEMS_NOT_PROCESSED_RETRY_FAILED_INTRO}
              </Typography>
              <Typography variant="body-base">
                {ITEMS_NOT_PROCESSED_RETRY_FAILED_ACTION}
              </Typography>
            </div>
          ) : (
            <Typography variant="body-base">
              {ITEMS_NOT_PROCESSED_MESSAGE}
            </Typography>
          )}
        </Alert>
      </div>
      <div className="sui-space-y-6">
        {items.map((item, index) => (
          <ReturnItemRow key={`${item.lineItemId}-${index}`} item={item} />
        ))}
        <LoadingButton
          variant="secondary"
          fullWidth
          onClick={onRetry}
          loading={retrying}
        >
          Try Again
        </LoadingButton>
        {retryFailed && onReturnToOrderDetails && (
          <Button
            variant="secondary"
            fullWidth
            type="button"
            onClick={onReturnToOrderDetails}
          >
            Return to Order Details
          </Button>
        )}
      </div>
    </ReturnReviewCard>
  );
};

export default ItemsNotProcessedCard;
